import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { BANK_QR_PAYMENT_CODES } from '../payment-compatibility.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, NonNullableFormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { finalize, forkJoin, Observable } from 'rxjs';
import { BoardPaymentService } from '../../../services/board-payment.service';
import {
  BankQrSettings,
  PaymentIntegration,
  PaymentIntegrationCode,
  PaymentServiceCreatePayload,
  PaymentServiceRecord,
  PaymentServiceXData,
  PaymentTypeRecord,
} from '../payments.types';
import {
  BANK_QR_LINK_TTL_DEFAULT,
  BANK_QR_NAME_MAX_LENGTH,
  BANK_QR_LINK_TTL_MAX,
  BANK_QR_PURPOSE_MAX_LENGTH,
  isTaxId,
  isUaIban,
  normalizeIban,
  PAYMENT_DIRECTORY_NAME_MAX_LENGTH,
} from '../payments.utils';
import { PAYMENT_SORT_ORDER_MAX, parseSortOrder } from '../../../modules/crm/board-payments/components/payment-directory.utils';

/** Errors are keys of `input_errors`, the way `app-input` shows them */
const notBlank: ValidatorFn = (control) => (String(control.value ?? '').trim() ? null : { incorrect: true });
const iban: ValidatorFn = (control) => (!control.value || isUaIban(control.value) ? null : { iban: true });
const taxId: ValidatorFn = (control) => (!control.value || isTaxId(control.value) ? null : { tax_id: true });
const integer: ValidatorFn = (control) =>
  control.value === null || Number.isInteger(control.value) ? null : { integer: true };
/** An empty order keeps the saved one, a new service goes last */
const sortOrder: ValidatorFn = (control: AbstractControl<string>): ValidationErrors | null => {
  const text = control.value.trim();
  if (!text || parseSortOrder(text) !== null) return null;

  return /^\d+$/.test(text) ? { max: { max: PAYMENT_SORT_ORDER_MAX } } : { integer: true };
};

/** `x_data` in one shape and key order (jsonb reorders the keys), so a saved one compares with the form */
const toXData = (data: PaymentServiceXData | null | undefined): Required<PaymentServiceXData> => {
  const settings = data?.integration ? data.settings : null;
  return {
    integration: data?.integration ?? null,
    settings: settings
      ? {
          legal_name: settings.legal_name,
          tax_id: settings.tax_id,
          iban: settings.iban,
          purpose: settings.purpose,
          link_ttl_days: settings.link_ttl_days,
          payment_type_id: settings.payment_type_id,
        }
      : null,
  };
};

/** Sidebar that creates or edits a payment service and connects it to an integration; closes with the saved service */
@Component({
  selector: 'app-payment-service-form',
  templateUrl: './payment-service-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full' },
})
export class PaymentServiceFormComponent implements OnInit {
  /** Null creates a new service */
  @Input() service: PaymentServiceRecord | null = null;

  @Output() close = new EventEmitter<PaymentServiceRecord | void>();

  private readonly boardPaymentService = inject(BoardPaymentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);

  protected readonly purposeMaxLength = BANK_QR_PURPOSE_MAX_LENGTH;
  protected readonly linkTtlMax = BANK_QR_LINK_TTL_MAX;
  protected readonly digitsOnly = /\d/;

  protected readonly settingsForm = this.fb.group({
    legal_name: ['', [Validators.required, notBlank, Validators.maxLength(BANK_QR_NAME_MAX_LENGTH)]],
    tax_id: ['', [Validators.required, taxId]],
    iban: ['', [Validators.required, iban]],
    purpose: ['', [Validators.required, notBlank, Validators.maxLength(BANK_QR_PURPOSE_MAX_LENGTH)]],
    link_ttl_days: this.fb.control<number | null>(BANK_QR_LINK_TTL_DEFAULT, [
      Validators.required,
      Validators.min(1),
      Validators.max(BANK_QR_LINK_TTL_MAX),
      integer,
    ]),
    payment_type_id: this.fb.control<string | null>(null, Validators.required),
  });
  protected readonly form = this.fb.group({
    name: ['', [Validators.required, notBlank, Validators.maxLength(PAYMENT_DIRECTORY_NAME_MAX_LENGTH)]],
    /** A service works only through its integration */
    integration: this.fb.control<PaymentIntegrationCode | null>(null, Validators.required),
    sort_order: ['', sortOrder],
    is_active: [true],
    settings: this.settingsForm,
  });

  protected integrationOptions: PaymentIntegration[] = [];
  /** Active payment types and the one the service has, even if it is inactive now */
  protected paymentTypeOptions: PaymentTypeRecord[] = [];
  protected loading = true;
  protected saving = false;

  protected get integration(): PaymentIntegrationCode | null {
    return this.form.controls.integration.value;
  }


  ngOnInit(): void {
    this.fill();
    this.form.controls.integration.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((integration) => this.onIntegration(integration));

    forkJoin({
      integrations: this.boardPaymentService.getPaymentIntegrations(),
      types: this.boardPaymentService.getPaymentTypes(),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: ({ integrations, types }) => this.setOptions(integrations, types),
        error: (error) => this.boardPaymentService.error(error),
      });
  }

  protected save(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    const change$ = this.service ? this.update(this.service) : this.create();
    if (!change$) {
      this.close.emit();
      return;
    }

    this.saving = true;
    change$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (service) => {
        this.boardPaymentService.success('board_payments.directories.services.saved');
        this.close.emit(service);
      },
      error: (error) => this.boardPaymentService.error(error),
    });
  }

  private create(): Observable<PaymentServiceRecord> {
    const { sort_order, ...payload } = this.payload();
    // Left out when empty, the backend puts the service last
    const order = parseSortOrder(sort_order);
    return this.boardPaymentService.createPaymentService({ ...payload, sort_order: order ?? undefined });
  }

  /** The request with the changed fields only, null when nothing changed */
  private update(saved: PaymentServiceRecord): Observable<PaymentServiceRecord> | null {
    const { sort_order, ...payload } = this.payload();
    const changes: Partial<PaymentServiceCreatePayload> = {};
    const order = parseSortOrder(sort_order);
    if (payload.name !== saved.name) changes.name = payload.name;
    if (payload.is_active !== saved.is_active) changes.is_active = payload.is_active;
    if (order !== null && order !== saved.sort_order) changes.sort_order = order;
    // `x_data` goes as a whole: the backend replaces the saved one with it
    if (JSON.stringify(payload.x_data) !== JSON.stringify(toXData(saved.x_data))) changes.x_data = payload.x_data;

    return Object.keys(changes).length ? this.boardPaymentService.updatePaymentService(saved.id, changes) : null;
  }

  private payload(): Required<Omit<PaymentServiceCreatePayload, 'sort_order'>> & { sort_order: string } {
    const value = this.form.getRawValue();
    return {
      name: value.name.trim(),
      is_active: value.is_active,
      sort_order: value.sort_order,
      x_data: toXData({
        integration: value.integration,
        settings: value.integration === 'bank_qr' ? this.bankQrSettings() : null,
      }),
    };
  }

  private bankQrSettings(): BankQrSettings {
    const value = this.settingsForm.getRawValue();
    return {
      legal_name: value.legal_name.trim(),
      tax_id: value.tax_id.trim(),
      iban: normalizeIban(value.iban),
      purpose: value.purpose.trim(),
      link_ttl_days: value.link_ttl_days ?? BANK_QR_LINK_TTL_DEFAULT,
      payment_type_id: value.payment_type_id,
    };
  }

  private fill(): void {
    const service = this.service;
    if (service) {
      this.form.patchValue({
        name: service.name,
        integration: service.x_data?.integration ?? null,
        sort_order: String(service.sort_order),
        is_active: service.is_active,
      });
      if (service.x_data?.settings) this.settingsForm.patchValue(service.x_data.settings);
    }
    this.toggleSettings(this.integration);
  }

  /** A picked integration names an empty service after itself */
  private onIntegration(integration: PaymentIntegrationCode | null): void {
    this.toggleSettings(integration);
    const picked = this.integrationOptions.find((option) => option.code === integration);
    if (!integration || !picked) return;

    const { name } = this.form.controls;
    if (!name.value.trim()) name.setValue(picked.name);
  }

  /** The settings are validated and sent only for the integration they belong to */
  private toggleSettings(integration: PaymentIntegrationCode | null): void {
    if (integration === 'bank_qr') this.settingsForm.enable({ emitEvent: false });
    else this.settingsForm.disable({ emitEvent: false });
  }

  private setOptions(integrations: PaymentIntegration[], types: PaymentTypeRecord[]): void {
    this.integrationOptions = integrations;
    // A new service gets the only integration there is right away
    if (!this.service && integrations.length === 1) this.form.controls.integration.setValue(integrations[0].code);
    const typeId = this.settingsForm.controls.payment_type_id.value;
    this.paymentTypeOptions = types.filter((type) => (type.is_active || type.id === typeId)
      && (type.fiscal_code == null || (BANK_QR_PAYMENT_CODES as readonly number[]).includes(type.fiscal_code)));
    if (typeId && !this.paymentTypeOptions.some((type) => type.id === typeId)) this.settingsForm.controls.payment_type_id.setValue(null);
  }
}
